import { Box, Button } from "@mui/material";
import ScrollHintRail from "global/components/ScrollHintRail/ScrollHintRail";
import strings from "global/constants/StringConstants";
import inboxStyles from "./Inbox.styles";
import { InboxTabKey } from "./InboxRowHelper";
import { tourAttr } from "tour";

const styles = inboxStyles;

/**
 * The design's buckets. "In progress" is every `Pending` request — the API
 * stores one status per request and can't separate "your turn" from "waiting on
 * others", so those are one tab rather than two. Each tab maps to a single
 * status via `INBOX_TAB_QUERY` and pages one request at a time on the server.
 */
export const INBOX_TABS: { key: InboxTabKey; label: string }[] = [
  { key: "all", label: strings.inboxTabLabels.all },
  { key: "waiting", label: strings.inboxTabLabels.waiting },
  { key: "completed", label: strings.inboxTabLabels.completed },
  { key: "declined", label: strings.inboxTabLabels.declined },
  // "Closed" (voided / expired) is intentionally not a filter tab; those rows
  // still surface under "All". The `closed` key stays in the type and
  // `INBOX_TAB_QUERY` because `getInboxRowState` still tags such rows with it.
];

interface InboxFilterTabsProps {
  value: InboxTabKey;
  /**
   * Badge totals, per the design. Optional because tallying all five buckets
   * costs a fetch of every bucket, which is the reason the screen used to load
   * every page up front — pass them once the API can count a bucket directly
   * (see the Backend Requests note) and the badges come back.
   */
  counts?: Partial<Record<InboxTabKey, number>>;
  disabled: boolean;
  onChange: (value: InboxTabKey) => void;
}

const InboxFilterTabs = ({
  value,
  counts,
  disabled,
  onChange,
}: InboxFilterTabsProps) => (
  <ScrollHintRail
    sx={styles.tabsRail}
    /*
      The tour marker goes on the scroller, not the wrapper: `inbox.tabs`
      spotlights the strip of pills, and the wrapper also covers the chevrons.
    */
    railProps={{
      ...tourAttr("inbox.tabs"),
      role: "group",
      "aria-label": "Filter requests",
    }}
    railSx={styles.tabs}
    previousLabel="Show previous filters"
    nextLabel="Show more filters"
  >
    {INBOX_TABS.map((tab) => {
      const active = value === tab.key;
      const count = counts?.[tab.key];
      return (
        <Button
          key={tab.key}
          disableRipple
          disabled={disabled}
          aria-pressed={active}
          onClick={() => onChange(tab.key)}
          sx={styles.tab(active)}
        >
          {tab.label}
          {count !== undefined && (
            <Box component="span" sx={styles.tabCount(active)}>
              {count}
            </Box>
          )}
        </Button>
      );
    })}
  </ScrollHintRail>
);

export default InboxFilterTabs;
